import { getBrowserTimeZone, getBrowserUtcOffsetMinutes } from '@/helpers/cron'
import { axiosParamsSerializer } from '@/helpers/formatter'
import { IClusterBilling, IClusterBillingCheck } from '@/models/DTO/ClusterBillingDTO'
import { ExportTaskDTO } from '@/models/DTO/ExportTaskDTO'
import {
  IAlarmsDashboardExport,
  IBaseExport,
  IDownloadExportFilter,
  IExportAbruptVariationCustomReport,
  IExportCceeAlerts,
  IExportCceePerformance,
  IExportCceeReadings,
  IExportCceeSubmissions,
  IExportIagdCompleteness,
  IExportIagdRanking,
  IExportInvoice,
  IExportMeters,
  IExportMetersAlarms,
  IExportGroupDicFicMonthly,
  IExportMetersByEffectivenessStatus,
  IExportMetersByGroup,
  IExportGroupLatestQualityParameters,
  IExportGroupQualityParameters,
  IExportReportDaily,
  IExportSMCs,
  IExportServiceNotes,
  IExportTickets,
  IQualityParametersExportFilters,
  ISMCStatistics,
} from '@/models/downloadsExports'
import {
  IExportBilling,
  IExportFrozenData,
  IExportMassMemory,
  IExportQee,
} from '@/models/downloadsExports'
import { PaginationDTO } from '@/models/table'
import axios from 'axios'
import { connect } from 'echarts'

const apiBaseUrl = process.env.VUE_APP_API_URL + '/export'

const removeNullParams = (params: Record<string, any>) => {
  Object.keys(params).forEach(
    (key) => (params[key] == null || params[key] === undefined) && delete params[key],
  )
  return params
}

export const exportMetersByGroup = async (params: IExportMetersByGroup) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-meters-by-group`, { params })
}

export const exportGroupQualityParameters = async (params: IExportGroupQualityParameters) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-group-quality-parameters`, { params })
}

export const exportGroupLatestQualityParameters = async (params: IExportGroupLatestQualityParameters) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-group-latest-quality-parameters`, { params })
}

export const exportGroupDicFicMonthly = async (params: IExportGroupDicFicMonthly) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-group-dic-fic-monthly`, { params })
}

export const exportMeterMassMemory = async (params: IExportMassMemory) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-readings`, { params })
}

export const exportMeterFrozenData = async (params: IExportFrozenData) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-frozen-data`, { params })
}

export const exportMeterQee = async (params: IExportQee) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-qee`, { params })
}

export const exportQeeMonthly = async (params: IExportQee) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-qee-monthly`, { params })
}

export const exportQeeDicFicDmic = async (params: IExportQee) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-qee-dic-fic-dmic`, { params })
}

export const exportInvoice = async (params: IExportInvoice) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-invoice`, { params })
}

export const exportReportDaily = async (params: IExportReportDaily) => {
  removeNullParams(params)
  return axios.get(`${apiBaseUrl}/report-daily-avaliable`, { params })
}

export const exportMetersBilling = async (params: IExportBilling) => {
  const { file, ...queryParams } = params
  const exportParams = {
    ...queryParams,
    timeZone: queryParams.timeZone ?? getBrowserTimeZone(),
    utcOffsetMinutes: queryParams.utcOffsetMinutes ?? getBrowserUtcOffsetMinutes(),
  }
  removeNullParams(exportParams)

  return axios.post<{ taskId: string; message: string }>(`${apiBaseUrl}/export-billings`, file, {
    params: exportParams,
  })
}

export const preProcessBillings = async (billings: IClusterBilling[], dateTime: Date) => {
  const params = { dateTime }
  removeNullParams(params)

  const body = billings.map((b) => ({
    serial: b.Serial,
    dateTime: b.DateTime,
  }))

  return axios.post<IClusterBillingCheck[]>(`${apiBaseUrl}/check-billings`, body, { params })
}

export const exportTickets = async (params: IExportTickets) => {
  const exportParams = {
    ...params,
    timeZone: params.timeZone ?? getBrowserTimeZone(),
    utcOffsetMinutes: params.utcOffsetMinutes ?? getBrowserUtcOffsetMinutes(),
  }

  removeNullParams(exportParams)

  return axios.get(`${apiBaseUrl}/export-tickets`, {
    params: exportParams,
    paramsSerializer: axiosParamsSerializer,
  })
}

export const exportServiceNotes = async (params: IExportServiceNotes) => {
  const queryParams = {
    company: params.company,
    connectionId: params.connectionId,
  }
  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-service-notes`, {
    params,
    paramsSerializer: axiosParamsSerializer,
  })
}

export const exportServiceNotesByServiceType = async (params: IExportServiceNotes) => {
  const queryParams: Record<string, any> = {
    connectionId: params.connectionId,
    actionType: params.actionType, // número: ex 2
    company: params.company,
    initialDate: params.initialDate,
    endDate: params.endDate,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-service-notes-by-service-type`, {
    params: queryParams,
    paramsSerializer: axiosParamsSerializer,
  })
}

export const exportServiceNotesByStatus = async (params: IExportServiceNotes) => {
  const queryParams = {
    status: params.actionType,
    company: params.company,
    connectionId: params.connectionId,
  }
  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-service-notes-by-status`, {
    params,
    paramsSerializer: axiosParamsSerializer,
  })
}

export const exportServiceNotesByStatusSap = async (params: IExportServiceNotes) => {
  const queryParams = {
    statusSap: params.statusSap,
    company: params.company,
    connectionId: params.connectionId,
  }
  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-service-notes-by-status-sap`, {
    params,
    paramsSerializer: axiosParamsSerializer,
  })
}

export const exportMeterAlarms = async (params: IExportMetersAlarms) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-meter-alarms`, { params })
}

export const exportBillingMeters = async (params: IExportMeters) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-meters`, { params })
}

export const exportMeters = async (params: IExportMeters) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-meters-without-billing`, { params })
}

export const exportIagdRanking = async (params: IExportIagdRanking) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-iagd-ranking`, {
    params: { ...params, distributorId: process.env.VUE_APP_PROVIDER },
  })
}

export const exportIagdCompleteness = async (params: IExportIagdCompleteness) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-iagd-completeness`, {
    params: { ...params, distributorId: process.env.VUE_APP_PROVIDER },
  })
}

export const exportSMCs = async (params: IExportSMCs) => {
  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/export-smcs`, { params })
}

export const exportSMCStatistics = async (params: ISMCStatistics) => {
  const queryParams = {
    page: params.page.page,
    pageSize: params.page.pageSize,
    startDate: params.filter.startDate,
    endDate: params.filter.endDate,
    wasRead: params.filter.wasRead,
    searchString: params.filter.searchString,
    visibleId: params.filter.visibleId,
    connectionId: params.connectionId,
  }
  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-smc-statistics`, { params: queryParams })
}

export const exportSMIStatistics = async (params: ISMCStatistics) => {
  const queryParams = {
    page: params.page.page,
    pageSize: params.page.pageSize,
    startDate: params.filter.startDate,
    endDate: params.filter.endDate,
    wasRead: params.filter.wasRead,
    searchString: params.filter.searchString,
    visibleId: params.filter.visibleId,
    connectionId: params.connectionId,
  }
  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-smi-statistics`, { params: queryParams })
}

export const exportSMCAlarms = async (params: ISMCStatistics) => {
  const queryParams = {
    page: params.page.page,
    pageSize: params.page.pageSize,
    type: params.filter.type,
    startDate: params.filter.startDate,
    endDate: params.filter.endDate,
    wasRead: params.filter.wasRead,
    searchString: params.filter.searchString,
    sortBy: params.filter.sortBy,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-smc-alarms`, { params: queryParams })
}

export const exportSMIAlarms = async (params: ISMCStatistics) => {
  const queryParams = {
    page: params.page.page,
    pageSize: params.page.pageSize,
    type: params.filter.type,
    startDate: params.filter.startDate,
    endDate: params.filter.endDate,
    wasRead: params.filter.wasRead,
    searchString: params.filter.searchString,
    sortBy: params.filter.sortBy,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-smi-alarms`, { params: queryParams })
}

export const exportSMCAlarmsDashboard = async (params: IAlarmsDashboardExport) => {
  const queryParams = {
    connectionId: params.connectionId,
    page: params.page,
    pageSize: params.pageSize,
    types: params.types,
    startDateTime: params.startDate,
    endDateTime: params.endDate,
    wasRead: params.wasRead,
    isCritical: params.isCritical,
    requiresEmailNotification: params.requiresEmailNotification,
    searchString: params.searchString,
    sortBy: params.sortBy,
  }

  removeNullParams(queryParams)

  return axios.get<{ taskId: string; message: string }>(
    `${apiBaseUrl}/export-smc-alarms-dashboard`,
    { params: queryParams, paramsSerializer: axiosParamsSerializer },
  )
}

export const exportSMIAlarmsDashboard = async (params: IAlarmsDashboardExport) => {
  const queryParams = {
    connectionId: params.connectionId,
    page: params.page,
    pageSize: params.pageSize,
    types: params.types,
    visibleIds: params.visibleIds,
    startDateTime: params.startDate,
    endDateTime: params.endDate,
    wasRead: params.wasRead,
    isCritical: params.isCritical,
    requiresEmailNotification: params.requiresEmailNotification,
    searchString: params.searchString,
    sortBy: params.sortBy,
  }

  removeNullParams(queryParams)

  return axios.get<{ taskId: string; message: string }>(
    `${apiBaseUrl}/export-smi-alarms-dashboard`,
    { params: queryParams, paramsSerializer: axiosParamsSerializer },
  )
}

export const exportDrpViolations = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-drp-violations`, { params: queryParams })
}

export const exportDrcViolations = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-drc-violations`, { params: queryParams })
}

export const exportReadingsMonthly = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-readings-monthly`, { params: queryParams })
}

export const exportDrpDrcAverage = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-drp-drc-average`, { params: queryParams })
}

export const exportViolationsMeasures = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-violations-measures`, { params: queryParams })
}

export const exportTensionDetailed = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    voltage: params.voltage,
    serial: params.serial,
    manufacturers: params.manufacturers,
    installation: params.installation,
    connectionId: params.connectionId,
    requiresEmailNotification: params.requiresEmailNotification,
  }

  removeNullParams(queryParams)

  return axios.get<{ taskId: string; message: string }>(`${apiBaseUrl}/export-tension-detailed`, {
    params: queryParams,
  })
}

export const exportWorstDrpDrcRanking = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    manufacturers: params.manufacturers,
    installation: params.installation,
    connectionId: params.connectionId,
    requiresEmailNotification: params.requiresEmailNotification,
  }

  removeNullParams(queryParams)

  return axios.get<{ taskId: string; message: string }>(
    `${apiBaseUrl}/export-worst-drp-drc-ranking`,
    { params: queryParams },
  )
}

export const exportTensionMonthlySummary = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    voltage: params.voltage,
    serial: params.serial,
    manufacturers: params.manufacturers,
    installation: params.installation,
    connectionId: params.connectionId,
    requiresEmailNotification: params.requiresEmailNotification,
  }

  removeNullParams(queryParams)

  return axios.get<{ taskId: string; message: string }>(
    `${apiBaseUrl}/export-tension-monthly-summary`,
    { params: queryParams },
  )
}

export const exportWorstIndicatorsRanking = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    manufacturers: params.manufacturers,
    installation: params.installation,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-worst-indicators-ranking`, { params: queryParams })
}

export const exportMonthlyQualityDicFic = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-monthly-quality-dic-fic`, { params: queryParams })
}

export const exportDetailedQeeIndicators = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-detailed-qee-indicators`, { params: queryParams })
}

export const exportDetailedPendencyDrpDrc = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    installation: params.installation,
    manufacturers: params.manufacturers,
    cities: params.cities,
    states: params.states,
    categories: params.categories,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-detailed-pendency-drp-drc`, { params: queryParams })
}

export const exportDicFicDetails = async (params: IQualityParametersExportFilters) => {
  const queryParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    serial: params.serial,
    connectionId: params.connectionId,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-dic-fic-details`, { params: queryParams })
}

export const getExportFiles = async (params: IDownloadExportFilter) => {
  removeNullParams(params)

  return axios.get<PaginationDTO<ExportTaskDTO>>(`${apiBaseUrl}`, {
    params,
  })
}

export const getExportTaskById = async (taskId: string) => {
  return axios.get<ExportTaskDTO>(`${apiBaseUrl}/${taskId}`)
}

export const downloadFile = async (taskId: string) => {
  const params = {
    taskId,
  }

  removeNullParams(params)

  return axios.get(`${apiBaseUrl}/download`, {
    params,
    responseType: 'blob' as 'json',
  })
}

export const exportMetersByEffectivenessStatus = async (
  params: IExportMetersByEffectivenessStatus,
) => {
  const queryParams = {
    connectionId: params.connectionId,
    date: params.date,
    effectivenessType: params.effectivenessType,
    sortOrder: params.sortOrder,
    searchString: params.searchString,
    page: params.page,
    pageSize: params.pageSize,
    startDateTime: params.startDateTime,
    endDateTime: params.endDateTime,
    comissioned: params.comissioned,
    type: params.type,
    modemSignalQuality: params.modemSignalQuality,
    visibleTags: params.visibleTags,
    status: params.status,
    manufacturers: params.manufacturers,
    networkTech: params.networkTech,
    estados: params.estados,
    cidades: params.cidades,
    requiresEmailNotification: params.requiresEmailNotification,
  }

  removeNullParams(queryParams)

  return axios.get(`${apiBaseUrl}/export-meters-by-effectiveness-status`, {
    params: queryParams,
    paramsSerializer: axiosParamsSerializer,
  })
}

export const exportAbruptVariationCustomReport = async (
  params: IExportAbruptVariationCustomReport,
) => {
  removeNullParams(params)
  return axios.get(`${apiBaseUrl}/export-dashboard-charts`, { params })
}

export const exportCceeSubmissions = async (params: IExportCceeSubmissions) => {
  removeNullParams(params)

  return axios.get<{ taskId: string; message: string }>(`${apiBaseUrl}/export-ccee-submissions`, {
    params,
  })
}

export const exportCceeOfflineMeters = async (params: IBaseExport) => {
  removeNullParams(params)

  return axios.get<{ taskId: string; message: string }>(`${apiBaseUrl}/export-ccee-offline-meters`, {
    params,
  })
}

export const exportCceePerformance = async (params: IExportCceePerformance) => {
  removeNullParams(params)

  return axios.get<{ taskId: string; message: string }>(`${apiBaseUrl}/export-ccee-performance`, {
    params,
  })
}

export const exportCceeAlerts = async (params: IExportCceeAlerts) => {
  removeNullParams(params)

  return axios.get<{ taskId: string; message: string }>(`${apiBaseUrl}/export-ccee-alerts`, {
    params,
  })
}

export const exportCceeReadings = async (params: IExportCceeReadings) => {
  removeNullParams(params)

  return axios.get<{ taskId: string; message: string }>(`${apiBaseUrl}/export-ccee-readings`, {
    params,
  })
}
